/* GASSISA
=========================================================================================
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 03/07/2026 | Lic. José Luis Aragón Cervantes  | Envío del identificador y token cargados en Parámetros de Caja
* ---------------------------------------------------------------------------------------
*/
import TextField from "../ui/TextField";
import { useEffect, useState } from "react"
import { Modal, Box, Typography, IconButton, Tooltip, Checkbox, FormControlLabel, Button } from "@mui/material";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import { useRequest } from "../../context/System/RequestContext"
import { useLayout } from "../../context/System/LayoutContext"
import { apiWhatsAppEnviar } from "../../settings/ApiConfig"

const modalStyle = {
    position: 'fixed',
    top: '10%',
    right: '15%',
    width: '68vw',
    maxHeight: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '9px',
    overflow: 'hidden'
};

const normalizarImagen = (imagen) => {
    if (!imagen || typeof imagen !== "string") return null;
    if (imagen.startsWith("data:") || imagen.startsWith("blob:") || imagen.startsWith("http")) return imagen;
    return `data:image/png;base64,${imagen}`;
};

const obtenerImagen = (articulo) => {
    if (!articulo) return null;
    const raw = articulo.imagen || articulo.foto || articulo.Imagen || articulo.Foto || articulo.IMAGEN || articulo.FOTO;
    return normalizarImagen(raw);
};

export default function WhatsAppEnvioModal({
    openModal,
    handleCloseModal,
    articulo,
    almacenexhibidor,
    caja,
    idPhoneWhatsApp,
    tokenAccesoWhatsApp,
}) {
    const { PostRequest } = useRequest()
    const { handleOpenAlert } = useLayout()

    const [telefono, setTelefono] = useState("")
    const [nombreContacto, setNombreContacto] = useState("")
    const [guardarContacto, setGuardarContacto] = useState(true)
    const [enviando, setEnviando] = useState(false)

    const getTelefonoValido = (raw) => {
        const d = raw.replace(/[^0-9]/g, "");
        return d.length === 10 || (d.length === 12 && d.startsWith("52"));
    };

    useEffect(() => {
        if (openModal) {
            setTelefono("")
            setNombreContacto("")
            setGuardarContacto(true)
            setEnviando(false)
        }
    }, [openModal])

    const medidas = articulo?.listamedidas || []
    const imagenSrc = obtenerImagen(articulo)

    const totalPares = medidas.reduce((sum, m) => sum + (m.existencia ?? m.Existencia ?? 0), 0);

    const construirMensaje = () => {
        const sep = "\u2500".repeat(36);
        let msg = `*SIZESOFTX \u2014 REPORTE DE ART\u00cdCULO*\n${sep}\n`;
        msg += `*Art\u00edculo:* ${articulo.clavearticulo}\n`;
        msg += `*Descripci\u00f3n:* ${articulo.descripcion}\n`;
        msg += `*Almac\u00e9n:* ${almacenexhibidor || "N/A"}\n\n`;
        msg += `*MEDIDA   EXISTENCIA   PRECIO*\n`;

        medidas.forEach((m) => {
            const existencia = m.existencia ?? m.Existencia ?? 0;
            const costo = m.preciodefinitivo ?? m.PrecioDefinitivo ?? m.costoventa ?? m.CostoVenta ?? m.pmv ?? m.PMV ?? 0;
            const medida = (m.medida ?? "").toString().padEnd(8);
            const existStr = existencia.toString().padStart(4);
            msg += `${medida}${existStr} pz    $${Number(costo).toFixed(2)}\n`;
        });

        msg += `${sep}\n`;
        msg += `*Total:* ${totalPares} pares\n`;
        msg += `${sep}\n`;
        msg += `Enviado desde SizeSoftX`;
        return msg;
    };

    const handleEnviar = async () => {
        if (!articulo?.clavearticulo) {
            handleOpenAlert("Seleccione un art\u00edculo antes de enviar el mensaje", "warning");
            return;
        }
        let tel = telefono.replace(/[^0-9]/g, "");
        if (tel.length === 12 && tel.startsWith("52")) {
            tel = tel.slice(2);
        }
        if (tel.length !== 10) {
            handleOpenAlert("Ingrese un n\u00famero de tel\u00e9fono v\u00e1lido de 10 d\u00edgitos", "error");
            return;
        }
        if (!idPhoneWhatsApp?.trim() || !tokenAccesoWhatsApp?.trim()) {
            handleOpenAlert("WhatsApp no est\u00e1 configurado en los par\u00e1metros de esta caja", "error");
            return;
        }

        setEnviando(true);
        let response;
        try {
            response = await PostRequest({
                url: apiWhatsAppEnviar,
                body: {
                    to: `52${tel}`,
                    clavearticulo: articulo.clavearticulo,
                    nombre: nombreContacto,
                    caja: caja ?? "",
                    almacenexhibidor,
                    guardarContacto,
                    idphonewhatsapp: idPhoneWhatsApp.trim(),
                    tokenaccesowhatsapp: tokenAccesoWhatsApp.trim(),
                },
                loader: "Enviando mensaje de WhatsApp..."
            });
        } finally {
            setEnviando(false);
        }

        if (!response || response.hasError) {
            handleOpenAlert(response?.message || "Error al enviar mensaje", "error");
            return;
        }

        handleOpenAlert("Mensaje de WhatsApp enviado correctamente", "OK");
        handleCloseModal();
    };

    const handleTelefonoChange = (e) => {
        const val = e.target.value;
        if (val.length > 0 && val[0] === "+") {
            const rest = val.slice(1).replace(/[^0-9]/g, "");
            setTelefono("+" + rest);
        } else {
            setTelefono(val.replace(/[^0-9]/g, ""));
        }
    };

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Box sx={{ backgroundColor: '#25D366', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsAppIcon sx={{ color: 'white', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            Enviar por WhatsApp
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseModal} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', flex: 1 }}>
                    {articulo && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {imagenSrc && (
                                <Box sx={{ width: 120, height: 120, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                                    <img src={imagenSrc} alt="Art\u00edculo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {articulo.clavearticulo}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {articulo.descripcion}
                                </Typography>
                                {articulo.familia && (
                                    <Typography variant="body2" color="text.secondary">
                                        {articulo.familia}{articulo.subfamilia ? ` / ${articulo.subfamilia}` : ""}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                    Almac\u00e9n: {almacenexhibidor || "N/A"}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ backgroundColor: '#f0fdf4', p: 1.5, borderRadius: 1, border: '1px solid #bbf7d0' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#166534' }}>
                            Vista previa del mensaje:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151' }}>
                            {articulo ? construirMensaje() : ""}
                        </Typography>
                    </Box>

                    <TextField autoComplete="off"
                        label="N\u00famero de tel\u00e9fono"
                        value={telefono}
                        onChange={handleTelefonoChange}
                        placeholder="+525541122334"
                        helperText="Ingrese el n\u00famero con c\u00f3digo de pa\u00eds (ej. +525541122334)"
                        size="small"
                        inputProps={{ maxLength: 14, inputMode: 'tel' }}
                    />

                    <TextField autoComplete="off"
                        label="Nombre del contacto"
                        value={nombreContacto}
                        onChange={(e) => setNombreContacto(e.target.value)}
                        placeholder="Nombre de la persona"
                        size="small"
                        helperText="Opcional \u2014 para identificar al contacto en promociones futuras"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={guardarContacto}
                                onChange={(e) => setGuardarContacto(e.target.checked)}
                                color="success"
                            />
                        }
                        label="Guardar contacto para futuras promociones"
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                        <Button variant="outlined" onClick={handleCloseModal} disabled={enviando}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleEnviar}
                            disabled={enviando || !getTelefonoValido(telefono) || !articulo?.clavearticulo}
                            startIcon={<WhatsAppIcon />}
                            sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1da851' } }}
                        >
                            {enviando ? "Enviando..." : "Enviar por WhatsApp"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}
